import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, Modal, Image, View, Text, Linking } from "react-native";
import { useRouter } from "expo-router";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import { signOut, updatePassword, EmailAuthProvider, linkWithCredential } from "firebase/auth";
import { doc, updateDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useMembers } from "@/hooks/useMembers";
import { removeItem } from "@/lib/storage";
import { colors, radius, font } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";
import ContentWidth, { ModalContent } from "@/components/ContentWidth";
import { ClubSkeleton } from "@/components/Skeleton";
import { DevSwitcherLink } from "@/components/DevSwitcher";
import styled from "styled-components/native";
import { UserIcon, CreditCardIcon, PeopleIcon, BarChartIcon, ActivityIcon, SettingsIcon, HelpIcon, MailIcon, FileIcon, KeyIcon, RefreshIcon, LogOutIcon, PlusIcon, TrashIcon, FilterIcon, InboxIcon } from "@/components/Icons";

/* ── styled components ─────────────────────────────────────────────── */

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const ClubHeader = styled.View`
  background-color: ${colors.white};
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
  padding-top: 24px;
  padding-bottom: 24px;
  align-items: center;
`;

const ClubLogo = styled.Image`
  width: 80px;
  height: 80px;
`;

const ClubName = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.3px;
  margin-top: 16px;
`;

const ClubSub = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
  margin-top: 4px;
`;

const InviteCtaText = styled.Text`
  color: #fff;
  font-size: 14px;
  font-weight: ${font.bold};
`;

const RoleCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 12px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 16px;
`;

const RoleLabel = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RoleValue = styled.Text`
  font-size: 16px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-top: 4px;
`;

const MenuCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const MenuRow = styled.View<{ hasBorder?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 16px;
  padding-vertical: 16px;
  border-bottom-width: ${(p) => (p.hasBorder ? "1px" : "0px")};
  border-bottom-color: ${colors.line};
`;

const MenuIcon = styled.View`
  width: 24px;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
`;

const MenuLabel = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MenuDetail = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  margin-right: 8px;
`;

const Chevron = styled.Text`
  font-size: 18px;
  color: ${colors.inkMuted};
`;

const Spacer = styled.View`
  flex: 1;
`;

const EmojiText = styled.Text`
  font-size: 16px;
`;

const AccountSection = styled.View`
  padding-horizontal: 20px;
  margin-top: 32px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const AccountCard = styled.View`
  margin-horizontal: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 16px;
  margin-bottom: 12px;
`;

const AccountLabel = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
`;

const AccountEmail = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  margin-top: 4px;
`;

const SetPasswordText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

const SetPasswordSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const SignOutText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.bold};
`;

const SwitchClubText = styled.Text`
  color: ${colors.ink};
  font-size: 14px;
  font-weight: ${font.bold};
`;

const ModalContainer = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 24px;
  padding-bottom: 40px;
`;

const ModalTitle = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ModalDesc = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  margin-bottom: 8px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const LabelWithMargin = styled(Label)`
  margin-top: 12px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  font-size: 15px;
  color: ${colors.ink};
`;

const FlexInput = styled(Input)`
  flex: 1;
`;

const ErrorBox = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 10px;
  margin-top: 12px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 13px;
  font-weight: ${font.semibold};
`;

const PrimaryBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const CancelBtnText = styled.Text`
  color: ${colors.inkMuted};
  font-size: 15px;
  font-weight: ${font.bold};
`;

const TeamRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.bg};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  margin-top: 8px;
  border-width: 1px;
  border-color: ${colors.line};
`;

const TeamRowInput = styled.TextInput`
  flex: 1;
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  padding-vertical: 0px;
`;

const TeamRowRemove = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
  padding-horizontal: 8px;
`;

const AddTeamRow = styled.View`
  flex-direction: row;
  gap: 8px;
  margin-top: 12px;
`;

const AddTeamBtnText = styled.Text`
  color: #fff;
  font-size: 14px;
  font-weight: ${font.bold};
`;

const SuccessWrapper = styled.View`
  align-items: center;
  padding-vertical: 20px;
  width: 100%;
`;

const SuccessEmoji = styled.Text`
  font-size: 28px;
  margin-bottom: 8px;
`;

const BottomSpacer = styled.View`
  height: 40px;
`;

/* ── component ─────────────────────────────────────────────────────── */

export default function ClubScreen() {
  const { user } = useAuth();
  const { club, membership, clubIds, switchClub, loading } = useClub();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { members } = useMembers(club?.id);
  const router = useRouter();
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  const [newTeam, setNewTeam] = useState("");
  const [editingTeams, setEditingTeams] = useState<string[]>([]);

  const [netsConfigured, setNetsConfigured] = useState(false);

  useEffect(() => {
    if (!club?.id) return;
    return onSnapshot(doc(db, "clubs", club.id, "settings", "nets"), (snap) => {
      setNetsConfigured(snap.exists() && snap.data()?.enabled !== false);
    });
  }, [club?.id]);

  const { confirmProps, confirm } = useConfirm();
  const isAdmin = membership?.role === "admin";
  const canManage = isAdmin || isAnyCaptain(user?.uid);
  const hasPassword = user?.providerData?.some((p) => p.providerId === "password") ?? false;
  const needsPaymentSetup = isAdmin && !club?.stripeOnboardingComplete && !club?.paymentsDeclined;

  const handleSetPassword = async () => {
    if (!user || !newPassword.trim()) return;
    if (newPassword !== confirmPassword) {
      setPwError("Passwords don't match.");
      return;
    }
    if (newPassword.length < 6) {
      setPwError("Password must be at least 6 characters.");
      return;
    }
    setPwSaving(true);
    setPwError("");
    try {
      if (hasPassword) {
        await updatePassword(user, newPassword);
      } else {
        const credential = EmailAuthProvider.credential(user.email!, newPassword);
        await linkWithCredential(user, credential);
      }
      setPwSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      if (err.code === "auth/requires-recent-login") {
        setPwError("Please sign out and sign in again before setting a password.");
      } else {
        setPwError(err.message || "Failed to set password.");
      }
    } finally {
      setPwSaving(false);
    }
  };

  // Sync editingTeams when modal opens or club teams change
  const openTeamsModal = () => {
    setEditingTeams([...(club?.teams || [])]);
    setShowTeams(true);
  };

  const handleRenameTeam = (index: number, value: string) => {
    const updated = [...editingTeams];
    updated[index] = value;
    setEditingTeams(updated);
  };

  const handleRemoveTeam = async (index: number) => {
    const team = editingTeams[index];
    const ok = await confirm({ title: `Remove ${team}?`, message: "This team will be removed from the list.", confirmLabel: "Remove", destructive: true });
    if (!ok) return;
    const updated = editingTeams.filter((_, i) => i !== index);
    setEditingTeams(updated);
  };

  const handleAddTeam = () => {
    if (!newTeam.trim()) return;
    setEditingTeams([...editingTeams, newTeam.trim()]);
    setNewTeam("");
  };

  const handleSaveTeams = async () => {
    if (!club) return;
    const cleaned = editingTeams.map((t) => t.trim()).filter(Boolean);
    await updateDoc(doc(db, "clubs", club.id), { teams: cleaned });
    setShowTeams(false);
  };

  if (loading) {
    return <View style={{ flex: 1, backgroundColor: colors.bg }}><ClubSkeleton /></View>;
  }

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
      {/* Club header */}
      <ClubHeader>
        {club?.logoUrl ? (
          <ClubLogo source={{ uri: club.logoUrl }} resizeMode="contain" />
        ) : (
          <StumpsIcon size={64} />
        )}
        <ClubName>{club?.name || "No Club"}</ClubName>
        <ClubSub>{club?.teams ? `${club.teams.length} teams` : ""}</ClubSub>
      </ClubHeader>

      {/* Invite members CTA */}
      {(membership?.role === "admin" || isAnyCaptain(user?.uid)) && (
        <Pressable
          style={({ pressed }) => [{
            marginHorizontal: 20,
            marginTop: 20,
            backgroundColor: colors.brand,
            borderRadius: radius.md,
            paddingVertical: 14,
            alignItems: "center" as const,
          }, pressed && { opacity: 0.9 }]}
          onPress={() => router.push("/(app)/members")}
        >
          <InviteCtaText>+ Invite members</InviteCtaText>
        </Pressable>
      )}

      {/* Profile completion nudge for existing members */}
      {membership && membership.profileComplete !== true && membership.profileComplete !== false && (
        <Pressable
          onPress={() => router.push("/(app)/member-profile")}
          style={({ pressed }) => ({
            marginHorizontal: 20,
            marginTop: 12,
            backgroundColor: colors.amber50,
            borderWidth: 1,
            borderColor: colors.amber200,
            borderRadius: radius.md,
            padding: 14,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            opacity: pressed ? 0.7 : 1,
          })}
        >
          <Text style={{ fontSize: 24 }}>👋</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: "700", color: colors.ink }}>Complete your profile</Text>
            <Text style={{ fontSize: 12, color: colors.inkMuted, marginTop: 2 }}>Add emergency contact and other details</Text>
          </View>
          <Text style={{ fontSize: 18, color: colors.inkMuted }}>›</Text>
        </Pressable>
      )}

      {/* Payment setup banner */}
      {needsPaymentSetup && (
        <View style={{
          marginHorizontal: 20,
          marginTop: 20,
          backgroundColor: colors.amber50,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderColor: colors.amber200,
          padding: 16,
        }}>
          <Text style={{ fontSize: 15, fontWeight: "700", color: colors.ink, marginBottom: 4 }}>
            Set up payments
          </Text>
          <Text style={{ fontSize: 13, color: colors.inkSecondary, lineHeight: 19, marginBottom: 12 }}>
            Connect your bank account so members can pay match fees and subs through the app.
          </Text>
          <Pressable
            onPress={() => router.push("/(app)/club-settings")}
            style={({ pressed }) => ({
              backgroundColor: colors.brand,
              borderRadius: radius.md,
              paddingVertical: 12,
              alignItems: "center" as const,
              opacity: pressed ? 0.9 : 1,
            })}
          >
            <Text style={{ color: "#fff", fontSize: 15, fontWeight: "700" }}>Set up now</Text>
          </Pressable>
          <Pressable
            onPress={async () => {
              if (!club) return;
              await updateDoc(doc(db, "clubs", club.id), { paymentsDeclined: true });
            }}
            style={{ marginTop: 10, alignItems: "center" as const }}
          >
            <Text style={{ fontSize: 13, color: colors.inkMuted }}>We don't collect payments online</Text>
          </Pressable>
        </View>
      )}

      {/* Your role */}
      {membership && (
        <RoleCard>
          <RoleLabel>Your role</RoleLabel>
          <RoleValue>{membership.role.charAt(0).toUpperCase() + membership.role.slice(1)}</RoleValue>
        </RoleCard>
      )}

      {/* Personal */}
      <MenuCard>
        {[
          { emoji: "👤", label: "My Profile", detail: "", onPress: () => router.push("/(app)/member-profile") },
          { emoji: "💰", label: "My Fees", detail: "", onPress: () => router.push("/(app)/my-fees") },
          ...(netsConfigured ? [{ emoji: "🏏", label: "Net Bookings", detail: "", onPress: () => router.push("/(app)/net-bookings") }] : []),
        ].map((item, i, arr) => (
          <Pressable
            key={item.label}
            style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
            onPress={item.onPress}
          >
            <MenuRow hasBorder={i < arr.length - 1}>
              <MenuIcon><EmojiText>{item.emoji}</EmojiText></MenuIcon>
              <MenuLabel>{item.label}</MenuLabel>
              <Spacer />
              {item.detail ? <MenuDetail>{item.detail}</MenuDetail> : null}
              <Chevron>›</Chevron>
            </MenuRow>
          </Pressable>
        ))}
      </MenuCard>

      {/* Club */}
      <SectionLabel style={{ marginTop: 24, marginHorizontal: 20 }}>Club</SectionLabel>
      <MenuCard>
        {[
          { emoji: "👥", label: "Members", detail: `${members.length}`, onPress: () => router.push("/(app)/members") },
          { emoji: "📊", label: "Finances", detail: "", onPress: () => router.push("/(app)/finances") },
        ].map((item, i, arr) => (
          <Pressable
            key={item.label}
            style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
            onPress={item.onPress}
          >
            <MenuRow hasBorder={i < arr.length - 1}>
              <MenuIcon><EmojiText>{item.emoji}</EmojiText></MenuIcon>
              <MenuLabel>{item.label}</MenuLabel>
              <Spacer />
              {item.detail ? <MenuDetail>{item.detail}</MenuDetail> : null}
              <Chevron>›</Chevron>
            </MenuRow>
          </Pressable>
        ))}
      </MenuCard>

      {/* Admin */}
      {isAdmin && (
        <>
          <SectionLabel style={{ marginTop: 24, marginHorizontal: 20 }}>Admin</SectionLabel>
          <MenuCard>
            {[
              { emoji: "🏏", label: "Teams", detail: `${club?.teams?.length || 0}`, onPress: () => router.push("/(app)/teams") },
              { emoji: "🏋️", label: "Training series", detail: "", onPress: () => router.push("/(app)/training-series") },
              { emoji: "💷", label: "Fee settings", detail: "", onPress: () => router.push("/(app)/fee-settings") },
              { emoji: "🥅", label: "Net bookings", detail: "", onPress: () => router.push("/(app)/net-settings") },
              { emoji: "💬", label: "Channel requests", detail: "", badge: false, onPress: () => router.push("/(app)/channel-requests") },
              { emoji: "⚙️", label: "Club settings", detail: "", badge: !!needsPaymentSetup, onPress: () => router.push("/(app)/club-settings") },
            ].map((item, i, arr) => (
              <Pressable
                key={item.label}
                style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
                onPress={item.onPress}
              >
                <MenuRow hasBorder={i < arr.length - 1}>
                  <MenuIcon><EmojiText>{item.emoji}</EmojiText></MenuIcon>
                  <MenuLabel>{item.label}</MenuLabel>
                  {item.badge && (
                    <View style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: colors.danger,
                      marginLeft: 6,
                    }} />
                  )}
                  <Spacer />
                  {item.detail ? <MenuDetail>{item.detail}</MenuDetail> : null}
                  <Chevron>›</Chevron>
                </MenuRow>
              </Pressable>
            ))}
          </MenuCard>
        </>
      )}

      {/* Support & Legal */}
      <SectionLabel style={{ marginTop: 24, marginHorizontal: 20 }}>Support & Legal</SectionLabel>
      <MenuCard>
        {[
          { emoji: "❓", label: "Help & FAQ", onPress: () => router.push("/(app)/help") },
          { emoji: "📧", label: "Contact Support", onPress: () => Linking.openURL("mailto:support@inthecrease.uk") },
          { emoji: "📄", label: "Privacy Policy", onPress: () => router.push({ pathname: "/(app)/legal", params: { page: "privacy" } }) },
          { emoji: "📋", label: "Terms of Service", onPress: () => router.push({ pathname: "/(app)/legal", params: { page: "terms" } }) },
        ].map((item, i, arr) => (
          <Pressable
            key={item.label}
            style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
            onPress={item.onPress}
          >
            <MenuRow hasBorder={i < arr.length - 1}>
              <MenuIcon><EmojiText>{item.emoji}</EmojiText></MenuIcon>
              <MenuLabel>{item.label}</MenuLabel>
              <Spacer />
              <Chevron>›</Chevron>
            </MenuRow>
          </Pressable>
        ))}
      </MenuCard>

      {/* Teams modal */}
      <Modal visible={showTeams} animationType="slide" transparent>
        <ModalContent onClose={() => setShowTeams(false)}>
          <ModalContainer>
            <ModalTitle>Teams</ModalTitle>
            <ModalDesc>Add or remove teams for your club.</ModalDesc>

            {editingTeams.map((team, i) => (
              <TeamRow key={i}>
                <TeamRowInput
                  value={team}
                  onChangeText={(v: string) => handleRenameTeam(i, v)}
                />
                <Pressable onPress={() => handleRemoveTeam(i)}>
                  <TeamRowRemove>✕</TeamRowRemove>
                </Pressable>
              </TeamRow>
            ))}

            <AddTeamRow>
              <FlexInput
                placeholder="e.g. 4th XI, Sunday XI"
                placeholderTextColor={colors.inkMuted}
                value={newTeam}
                onChangeText={setNewTeam}
                onSubmitEditing={handleAddTeam}
              />
              <Pressable
                style={({ pressed }) => [{
                  backgroundColor: colors.brand,
                  borderRadius: radius.md,
                  paddingHorizontal: 16,
                  justifyContent: "center" as const,
                }, pressed && { opacity: 0.9 }]}
                onPress={handleAddTeam}
                disabled={!newTeam.trim()}
              >
                <AddTeamBtnText>Add</AddTeamBtnText>
              </Pressable>
            </AddTeamRow>

            <Pressable
              style={({ pressed }) => [{
                backgroundColor: colors.brand,
                borderRadius: radius.card,
                paddingVertical: 16,
                alignItems: "center" as const,
                marginTop: 20,
              }, pressed && { opacity: 0.9 }]}
              onPress={handleSaveTeams}
            >
              <PrimaryBtnText>Save</PrimaryBtnText>
            </Pressable>

            <Pressable
              style={({ pressed }) => [{
                paddingVertical: 12,
                alignItems: "center" as const,
                marginTop: 8,
              }, pressed && { opacity: 0.7 }]}
              onPress={() => setShowTeams(false)}
            >
              <CancelBtnText>Cancel</CancelBtnText>
            </Pressable>
          </ModalContainer>
        </ModalContent>
      </Modal>

      {/* Account */}
      <SectionLabel style={{ marginTop: 24, marginHorizontal: 20 }}>Account</SectionLabel>
      <MenuCard>
        <MenuRow hasBorder>
          <MenuIcon><EmojiText>✉️</EmojiText></MenuIcon>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 12, color: colors.inkMuted, fontWeight: "600" }}>Signed in as</Text>
            <Text style={{ fontSize: 14, fontWeight: "700", color: colors.ink, marginTop: 2 }}>{user?.email}</Text>
          </View>
        </MenuRow>
        <Pressable style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]} onPress={() => router.push("/(app)/set-password")}>
          <MenuRow hasBorder>
            <MenuIcon><EmojiText>🔑</EmojiText></MenuIcon>
            <MenuLabel>{hasPassword ? "Change password" : "Set a password"}</MenuLabel>
            <Spacer />
            <Chevron>›</Chevron>
          </MenuRow>
        </Pressable>
        {clubIds.length > 1 && (
          <Pressable style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]} onPress={() => switchClub(null)}>
            <MenuRow hasBorder>
              <MenuIcon><EmojiText>🔄</EmojiText></MenuIcon>
              <MenuLabel>Switch club</MenuLabel>
              <Spacer />
              <Chevron>›</Chevron>
            </MenuRow>
          </Pressable>
        )}
        <Pressable style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]} onPress={() => router.push("/(app)/create-club")}>
          <MenuRow hasBorder>
            <MenuIcon><EmojiText>➕</EmojiText></MenuIcon>
            <MenuLabel>Create new club</MenuLabel>
            <Spacer />
            <Chevron>›</Chevron>
          </MenuRow>
        </Pressable>
        <Pressable
          style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
          onPress={async () => {
            const ok = await confirm({
              title: "Sign out?",
              message: "You'll need to sign in again to access your clubs.",
              confirmLabel: "Sign out",
              destructive: true,
            });
            if (!ok) return;
            try {
              const m = await import("@/lib/pushNotifications");
              if (user) await m.unregisterPushTokenForCurrentDevice(user.uid);
            } catch {}
            await removeItem("activeClubId");
            await signOut(auth);
          }}
        >
          <MenuRow hasBorder>
            <MenuIcon><EmojiText>👋</EmojiText></MenuIcon>
            <Text style={{ fontSize: 15, fontWeight: "600", color: colors.danger }}>Sign out</Text>
            <Spacer />
          </MenuRow>
        </Pressable>
        <Pressable
          style={({ pressed }) => [pressed && { backgroundColor: colors.bg }]}
          onPress={async () => {
            const ok = await confirm({
              title: "Delete your account?",
              message: "This will permanently delete your account and remove you from all clubs. This cannot be undone.",
              confirmLabel: "Delete account",
              destructive: true,
            });
            if (!ok) return;
            try {
              const { httpsCallable } = await import("firebase/functions");
              const { functions } = await import("@/lib/firebase");
              const deleteAccount = httpsCallable(functions, "deleteAccount");
              await deleteAccount({});
            } catch (err: any) {
              const msg = err?.message || "Failed to delete account.";
              await confirm({ title: "Cannot delete account", message: msg, confirmLabel: "OK" });
            }
          }}
        >
          <MenuRow hasBorder={false}>
            <MenuIcon><EmojiText>🗑️</EmojiText></MenuIcon>
            <Text style={{ fontSize: 15, fontWeight: "600", color: colors.danger }}>Delete account</Text>
            <Spacer />
          </MenuRow>
        </Pressable>
      </MenuCard>

      <DevSwitcherLink />

      <BottomSpacer />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
    </KeyboardAwareScroll>
  );
}
